import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {LoadingStates} from '@Utils/loading.util';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {closeAiActionLoadingModal, isCurrentModalSession, openMdeLoadingModalAndGetSessionId, showMdeReadyModal} from '@Components/ai-chat-window/lib/ai-action-loading-modal.library';
import {clearPendingInChatJob, isInChatJobHandedOffToGrowl, markInChatJobPending} from '@Components/ai-chat-window/lib/in-chat-job-handoff.library';
import {
  getMdeDesignUrl,
  getMdePosterUrl,
  isMdeInsufficientCreditsError,
  MDE_POLL_BUDGET_MS,
  openMdeDesignTab,
  openMdePendingEditorTab,
  pollMdeJob,
  showMdeDoneGrowl,
  showMdeErrorGrowl,
  showMdeOngoingGrowl,
  showMdeStillWorkingGrowl,
  startMdeJob,
} from '@Libraries/make-design-editable.library';
import {type MdeJobRun, MdeStartKind, type MdeStartResponse} from '@Libraries/make-design-editable.library.types';
import {assertNever} from '@Utils/assert-never.util';
import {ensureAiChatCreditsForCost} from '@Libraries/ai-chat.library';
import {refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {AiChatOpenEditorType, GA4EventName, GA4EventParamName, trackGA4CustomEvent} from '@Libraries/ga-events';

interface UseMakeDesignEditableProps {
  chatId: string;
}

interface AnnounceFinishedDesignOptions {
  navigationUrl: string;
  sessionId: string;
  isHandedOffToGrowl: boolean;
}

interface UseMakeDesignEditable {
  makeDesignEditable: (hashedId: string) => Promise<void>;
}

/** The row's spinner lives in the shared per-image state so every mounted edit menu agrees on which image is running. */
export const useMakeDesignEditable = ({chatId}: UseMakeDesignEditableProps): UseMakeDesignEditable => {
  const {state, actions} = useAiChat();

  const setLoadingState = (hashedId: string, loadingState: LoadingStates): void => {
    actions.updateLoadingStatesForImageActions(chatId, hashedId, {makeDesignEditableLoadingState: loadingState});
  };

  const failRun = (hashedId: string, error: unknown): void => {
    setLoadingState(hashedId, LoadingStates.NOT_LOADED);
    showMdeErrorGrowl(error);
    console.error(error);
  };

  const ensureCreditsForRun = async (hashedId: string): Promise<boolean> => {
    try {
      const isCovered = await ensureAiChatCreditsForCost(state.makeDesignEditableCreditCost);
      if (!isCovered) {
        setLoadingState(hashedId, LoadingStates.NOT_LOADED);
      }
      return isCovered;
    } catch (e: unknown) {
      failRun(hashedId, e);
      return false;
    }
  };

  const refreshCreditsAfterCharge = (): void => {
    actions.refreshCredits();
    refreshTeamCreditsInStore().catch((e: unknown): void => {
      console.error(e);
    });
  };

  const makeDesignEditable = async (hashedId: string): Promise<void> => {
    setLoadingState(hashedId, LoadingStates.LOADING);
    const start = await requestStart(hashedId);
    if (start !== null) {
      await followStart(hashedId, start);
    }
  };

  const requestStart = async (hashedId: string): Promise<MdeStartResponse | null> => {
    try {
      return await startMdeJob(hashedId);
    } catch (e: unknown) {
      if (isMdeInsufficientCreditsError(e)) {
        return buyCreditsAndStartAgain(hashedId);
      }
      failRun(hashedId, e);
      return null;
    }
  };

  const buyCreditsAndStartAgain = async (hashedId: string): Promise<MdeStartResponse | null> => {
    if (!(await ensureCreditsForRun(hashedId))) {
      return null;
    }
    try {
      return await startMdeJob(hashedId);
    } catch (e: unknown) {
      failRun(hashedId, e);
      return null;
    }
  };

  const followStart = async (hashedId: string, start: MdeStartResponse): Promise<void> => {
    switch (start.kind) {
      case MdeStartKind.REUSED:
        setLoadingState(hashedId, LoadingStates.LOADED);
        openReusedDesign(start.posterHashedId);
        return;
      case MdeStartKind.STARTED:
        refreshCreditsAfterCharge();
        await followRun(hashedId, start);
        return;
      case MdeStartKind.RESUMED:
        await followRun(hashedId, start);
        return;
      default:
        assertNever(start);
    }
  };

  /** Mobile, and desktop without a pre-created poster, waits in the chat; otherwise the editor tab polls. */
  const followRun = async (hashedId: string, {jobId, posterHashedId}: MdeJobRun): Promise<void> => {
    if (posterHashedId === null || window.innerWidth < MOBILE_MODAL_WIDTH_THRESHOLD) {
      await waitForJobInChat(hashedId, jobId);
      return;
    }
    setLoadingState(hashedId, LoadingStates.LOADED);
    trackEditableLayersOpen();
    openMdePendingEditorTab(jobId, posterHashedId);
  };

  /** Mobile and the desktop fallback. The ready modal is skipped when another AI action has taken the modal over. */
  const waitForJobInChat = async (hashedId: string, jobId: string): Promise<void> => {
    const pendingJobId = markInChatJobPending({showOngoingGrowl: showMdeOngoingGrowl});
    const sessionId = openMdeLoadingModalAndGetSessionId();

    try {
      const outcome = await pollMdeJob(jobId, MDE_POLL_BUDGET_MS);
      if (outcome === null) {
        settleStillRunningJob(hashedId, pendingJobId);
        return;
      }
      setLoadingState(hashedId, LoadingStates.LOADED);
      trackEditableLayersOpen();
      const isHandedOffToGrowl = releaseInChatJob(pendingJobId);
      announceFinishedDesign({navigationUrl: getMdePosterUrl(outcome), sessionId, isHandedOffToGrowl});
    } catch (e: unknown) {
      closeModalUnlessHandedOff(releaseInChatJob(pendingJobId));
      failRun(hashedId, e);
    }
  };

  /** The budget ran out but the job may still finish: not a failure, so the row is re-enabled and the user is told it is still working. */
  const settleStillRunningJob = (hashedId: string, pendingJobId: string): void => {
    closeModalUnlessHandedOff(releaseInChatJob(pendingJobId));
    setLoadingState(hashedId, LoadingStates.NOT_LOADED);
    showMdeStillWorkingGrowl();
  };

  return {makeDesignEditable};
};

const trackEditableLayersOpen = (): void => {
  trackGA4CustomEvent(GA4EventName.AICHAT_OPEN_EDITOR, {[GA4EventParamName.TYPE]: AiChatOpenEditorType.MAKE_DESIGN_EDITABLE});
};

const releaseInChatJob = (pendingJobId: string): boolean => {
  const isHandedOffToGrowl = isInChatJobHandedOffToGrowl(pendingJobId);
  clearPendingInChatJob(pendingJobId);
  return isHandedOffToGrowl;
};

const closeModalUnlessHandedOff = (isHandedOffToGrowl: boolean): void => {
  if (!isHandedOffToGrowl) {
    closeAiActionLoadingModal();
  }
};

const openReusedDesign = (posterHashedId: string): void => {
  trackEditableLayersOpen();
  if (window.innerWidth < MOBILE_MODAL_WIDTH_THRESHOLD) {
    showMdeReadyModal(getMdeDesignUrl(posterHashedId));
    return;
  }
  openMdeDesignTab(posterHashedId);
};

const announceFinishedDesign = ({navigationUrl, sessionId, isHandedOffToGrowl}: AnnounceFinishedDesignOptions): void => {
  if (isHandedOffToGrowl) {
    showMdeDoneGrowl(navigationUrl);
  } else if (isCurrentModalSession(sessionId)) {
    showMdeReadyModal(navigationUrl);
  }
};
